/*  ──────────────────────────────────────────────────────────────
    MyAccountTab.tsx
    • Only “SuperAdmin” (case–insensitive) can edit profile
      or change password.
    • All other roles see the same UI but everything is read-only.
    • Adds an "Add Balance" card (SuperAdmin only) that POSTs to
      /adminwalletadd and shows the updated balance returned by the server.
    ────────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Stack, Avatar, Chip, Typography, TextField,
  InputAdornment, IconButton, Button, Snackbar, Alert, CircularProgress
} from "@mui/material";
import {
  User, Mail, Phone, Globe, Eye, EyeOff, KeyRound, Lock,
  Edit, Save, X, Shield, Copy, Link as LinkIcon
} from "lucide-react";
import { apiFetch } from "../../utils/api";
import { useWhatsappRef, WaRef } from "../../hooks/waHook";
/* ---------- helpers & constants -------------------------------------- */
interface UserData {
  name: string;
  email: string;
  role: string;
  phone: string;
  country: string;
  agent_code?: string;
}

/* =========================================================================
   COMPONENT
   ========================================================================= */
export default function MyAccountTab() {

  // ── Grab staffId from localStorage (as number) ─────────────────────────
  const rawId = localStorage.getItem("currentUserId");
  const staffId = rawId ? parseInt(rawId, 10) : null;

  // ── Fetch the user’s WhatsApp referral info ─────────────────────────────
  const waRef: WaRef | null = useWhatsappRef(staffId);

  /* ── base state ──────────────────────────────────────────────────── */
  const [me, setMe] = useState<UserData | null>(null);
  const [editMode, setEM] = useState(false);
  const [editData, setED] = useState<Partial<UserData>>({});

  /* ── password section ───────────────────────────────────────────── */
  const [oldPwd, setOld] = useState("");
  const [newPwd, setNew] = useState("");
  const [showOld, setSO] = useState(false);
  const [showNew, setSN] = useState(false);

  /* ── balance state (new) ─────────────────────────────────────────── */
  const [balance, setBalance] = useState("");         // amount as string
  const [loadingBal, setLoadingBal] = useState(false);

  /* ── misc UI state ──────────────────────────────────────────────── */
  const [loadingPwd, setLoadPwd] = useState(false);
  const [savingProf, setSaving] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" }>({
    open: false, msg: "", sev: "success"
  });

  const canEdit = React.useMemo(() => {
    /** role from API wins, otherwise fall back to localStorage */
    const raw = (me?.role || localStorage.getItem("userRole") || "")
      .toLowerCase()
      .replace(/\s+/g, "");      /* "Super Admin" → "superadmin" */
    return raw === "superadmin";
  }, [me]);

  /* ── fetch my profile once ──────────────────────────────────────── */
  useEffect(() => {
    const id = localStorage.getItem("currentUserId");
    if (!id) return;
    apiFetch<UserData>(`/api/staff/${id}`)
      .then(res => setMe({
        name: res.name,
        email: res.email,
        role: res.role,
        phone: res.phone || "",
        country: res.country || "",
        agent_code: res.agent_code || ""
      }))
      .catch(() => {/* ignore – snack not needed on first load */});
  }, []);

  /* ─── helpers ───────────────────────────────────────────────────── */
  const startEdit = () => { if (canEdit) { setED(me ?? {}); setEM(true); } };
  const cancelEdit = () => { setEM(false); setED({}); };
  const field = (k: keyof UserData) => editData[k] ?? "";

  /* ─── save profile ──────────────────────────────────────────────── */
  const saveProfile = async () => {
    if (!canEdit) return;
    const id = localStorage.getItem("currentUserId");
    if (!id) return;

    setSaving(true);
    try {
      await apiFetch(`/api/staff/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: field("name"), phone: field("phone"), country: field("country") })
      });
      setMe(prev => prev ? { ...prev, ...editData } as UserData : prev);
      setSnack({ open: true, msg: "Profile updated!", sev: "success" });
      cancelEdit();
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || "Update failed", sev: "error" });
    } finally { setSaving(false); }
  };

  /* ─── change password ───────────────────────────────────────────── */
  const changePwd = async () => {
    if (!canEdit) return;
    if (!oldPwd || !newPwd)
      return setSnack({ open: true, msg: "Fill both password fields", sev: "error" });

    setLoadPwd(true);
    try {
      await apiFetch("/api/staff/password", {
        method: "PATCH",
        body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd })
      });
      setSnack({ open: true, msg: "Password updated!", sev: "success" });
      setOld(""); setNew("");
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || "Password change failed", sev: "error" });
    } finally { setLoadPwd(false); }
  };

  /* ─── copy text to clipboard ────────────────────────────────────── */
  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnack({ open: true, msg: `${label} copied`, sev: "success" });
    } catch {
      setSnack({ open: true, msg: "Copy failed", sev: "error" });
    }
  };

  /* ─── add balance (calls your /adminwalletadd endpoint) ──────────── */
const addBalance = async () => {
  if (!canEdit) return;
  if (!balance) return setSnack({ open: true, msg: "Enter a balance amount", sev: "error" });

  const amount = Number(balance);
  if (Number.isNaN(amount) || !isFinite(amount)) {
    return setSnack({ open: true, msg: "Enter a valid number", sev: "error" });
  }

  setLoadingBal(true);
  try {
    // Direct call to external API
    const res = await apiFetch("/adminwalletadd", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount })
    });

    // Expecting { message: "...", balance: { staff_id, inr } }
    const inr = res?.balance?.inr;
    if (typeof inr !== "undefined") {
      setSnack({ open: true, msg: `Balance updated — ₹${inr}`, sev: "success" });
    } else {
      setSnack({ open: true, msg: "Balance updated!", sev: "success" });
    }
    setBalance("");
  } catch (e: any) {
    const msg = e?.message || e?.error || "Failed to add balance";
    setSnack({ open: true, msg, sev: "error" });
  } finally {
    setLoadingBal(false);
  }
};

  /* ─── loading skeleton ──────────────────────────────────────────── */
  if (!me)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", bgcolor: "#0C0D1D" }}>
        <CircularProgress />
      </Box>
    );

  /* ===================================================================
     RENDER
     =================================================================== */
  return (
    <Box sx={{ p: 3, background: "#0C0D1D", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 800, mx: "auto" }}>
        <Stack spacing={3}>

          {/* ───────── Profile card ───────── */}
          <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #1E2D55",
            background: "#0E1831" }}>
            <CardContent sx={{ p: 4 }}>
              {/* header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Avatar sx={{ width: 80, height: 80, bgcolor: "#2B6EF5", fontSize: "2rem", fontWeight: 700 }}>
                    {(editMode ? field("name") : me.name).charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#ffffff", mb: 1 }}>
                      {editMode ? field("name") : me.name}
                    </Typography>
                    <Chip label={me.role} size="small"
                      sx={{ bgcolor: "rgba(100,110,205,0.15)", color: "#2B6EF5", fontWeight: 600 }} />
                  </Box>
                </Box>

                {/* edit buttons */}
                {canEdit && (
                  editMode ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton onClick={saveProfile} disabled={savingProf}
                        sx={{ bgcolor: "#10b981", color: "#fff", "&:hover": { bgcolor: "#059669" } }}>
                        {savingProf ? <CircularProgress size={20} /> : <Save size={20} />}
                      </IconButton>
                      <IconButton onClick={cancelEdit}
                        sx={{ bgcolor: "#dc2626", color: "#fff", "&:hover": { bgcolor: "#b91c1c" } }}>
                        <X size={20} />
                      </IconButton>
                    </Box>
                  ) : (
                    <IconButton onClick={startEdit}
                      sx={{ bgcolor: "#2B6EF5", color: "#fff", "&:hover": { bgcolor: "#4A85F7" } }}>
                      <Edit size={20} />
                    </IconButton>
                  )
                )}
              </Box>

              {/* grid of fields */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                {/* Name */}
                <ProfileRow icon={<User size={20} />} iconBg="#f0fdf4" iconColor="#16a34a"
                  label="Full Name" editMode={editMode}
                  value={editMode ? field("name") : me.name}
                  onChange={v => setED(d => ({ ...d, name: v }))} />
                {/* Email – readonly */}
                <ProfileRow icon={<Mail size={20} />} iconBg="#fffbeb" iconColor="#f59e0b"
                  label="Email" value={me.email} />
                {/* Phone */}
                <ProfileRow icon={<Phone size={20} />} iconBg="#f0f9ff" iconColor="#0ea5e9"
                  label="Phone Number" editMode={editMode}
                  value={editMode ? field("phone") : me.phone || "Not provided"}
                  onChange={v => setED(d => ({ ...d, phone: v }))} />
                {/* Country */}
                <ProfileRow icon={<Globe size={20} />} iconBg="#fdf2f8" iconColor="#ec4899"
                  label="Country" editMode={editMode}
                  value={editMode ? field("country") : me.country || "Not provided"}
                  onChange={v => setED(d => ({ ...d, country: v }))} />
              </Box>
            </CardContent>
          </Card>

          {/* ───────── Agent Code card ───────── */}
          {me.agent_code && (
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #1E2D55", bgcolor: "#0E1831" }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(43,110,245,0.15)", color: "#2B6EF5" }}>
                    <LinkIcon size={20} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffffff", fontSize: 18 }}>
                      Agent ID & Shareable Link
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                      Share this link or code — new players who sign up with it will be attached to you.
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#94a3b8", mb: 0.5, fontSize: 12 }}>
                      Agent ID
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        value={me.agent_code}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": { bgcolor: "#0C0D1D" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1E2D55" },
                          "& .MuiInputBase-input": { color: "#ffffff", fontFamily: "monospace", fontWeight: 600 }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => copyText(me.agent_code!, "Agent ID")}
                        startIcon={<Copy size={18} />}
                        sx={{ bgcolor: "#2B6EF5", "&:hover": { bgcolor: "#4A85F7" } }}
                      >
                        Copy
                      </Button>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#94a3b8", mb: 0.5, fontSize: 12 }}>
                      Shareable Link
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        value={`${window.location.origin}/referal/${me.agent_code}`}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": { bgcolor: "#0C0D1D" },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1E2D55" },
                          "& .MuiInputBase-input": { color: "#ffffff" }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => copyText(`${window.location.origin}/referal/${me.agent_code}`, "Link")}
                        startIcon={<Copy size={18} />}
                        sx={{ bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" } }}
                      >
                        Copy
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ───────── Password card (SuperAdmin only) ───────── */}
          {canEdit && (
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #1E2D55", bgcolor: "#0E1831" }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(220,38,38,0.15)", color: "#ef4444" }}>
                    <Lock size={20} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffffff", fontSize: 18 }}>
                      Change Password
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                      Update your password to keep your account secure
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={3}>
                  <PwdField label="Current Password"
                    value={oldPwd} onChange={setOld}
                    show={showOld} setShow={setSO} />
                  <PwdField label="New Password"
                    value={newPwd} onChange={setNew}
                    show={showNew} setShow={setSN} />

                  <Button variant="contained" disabled={loadingPwd || !oldPwd || !newPwd}
                    onClick={changePwd}
                    startIcon={loadingPwd ? <CircularProgress size={20} /> : <Shield size={20} />}
                    sx={{ py: 1.5, px: 4, borderRadius: 2, bgcolor: "#2B6EF5",
                      fontWeight: 600, alignSelf: "flex-start",
                      "&:hover": { bgcolor: "#4A85F7" },
                      "&:disabled": { bgcolor: "#1E2D55" } }}>
                    {loadingPwd ? "Updating…" : "Update Password"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ───────── Add Balance card (SuperAdmin only) ───────── */}
          {canEdit && (
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #1E2D55", bgcolor: "#0E1831" }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(16,185,129,0.15)", color: "#10b981" }}>
                    <Shield size={20} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#ffffff", fontSize: 18 }}>
                      Add Balance
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                      Credit balance to Superadmin Only
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Amount (INR)"
                    type="number"
                    value={balance}
                    onChange={e => setBalance(e.target.value)}
                    sx={{ flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#0C0D1D",
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2B6EF5" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2B6EF5" }
                      },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1E2D55" },
                      "& .MuiInputLabel-root": { color: "#94a3b8" },
                      "& .MuiInputBase-input": { color: "#ffffff" }
                    }}
                  />
                  <Button
                    variant="contained"
                    disabled={loadingBal || !balance}
                    onClick={addBalance}
                    startIcon={loadingBal ? <CircularProgress size={20} /> : <Save size={20} />}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#10b981", fontWeight: 600,
                      "&:hover": { bgcolor: "#059669" },
                      "&:disabled": { bgcolor: "#1E2D55" }
                    }}
                  >
                    {loadingBal ? "Adding…" : "Add Balance"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ───────── WhatsApp Referral Card ───────── */}
          {waRef && (
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #1E2D55", bgcolor: "#0E1831" }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#ffffff" }}>
                  Your WhatsApp Referral
                </Typography>
                <Typography sx={{ color: "#94a3b8" }}>
                  <strong style={{ color: "#ffffff" }}>Slug:</strong> {waRef.slug}
                </Typography>
                <Typography sx={{ color: "#94a3b8" }}>
                  <strong style={{ color: "#ffffff" }}>Phone:</strong> {waRef.phone}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    href={waRef.link}
                    target="_blank"
                    sx={{
                      textTransform: "none",
                      bgcolor: "#25D366", color: "#fff",
                      "&:hover": { bgcolor: "#128C7E" }
                    }}
                  >
                    Open in WhatsApp
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snack.sev} sx={{ borderRadius: 2, fontWeight: 500 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* =======================================================================
   REUSABLE SMALL SUB-COMPONENTS
   ======================================================================= */
type RowProps = {
  icon: JSX.Element;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  editMode?: boolean;
  onChange?: (v: string) => void;
};
function ProfileRow({ icon, iconBg, iconColor, label, value, editMode, onChange }: RowProps) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: iconBg, color: iconColor }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: 12 }}>{label}</Typography>
        {editMode && onChange
          ? <TextField variant="standard" value={value} onChange={e => onChange(e.target.value)}
            sx={{
              "& .MuiInput-underline:before": { borderBottomColor: "#1E2D55" },
              "& .MuiInput-underline:hover:before": { borderBottomColor: "#2B6EF5" },
              "& .MuiInput-underline:after": { borderBottomColor: "#2B6EF5" },
              "& .MuiInputBase-input": { color: "#ffffff" }
            }} />
          : <Typography variant="body1" sx={{ fontWeight: 600, color: "#ffffff" }}>{value}</Typography>}
      </Box>
    </Box>
  );
}

function PwdField({ label, value, onChange, show, setShow }: { label: string; value: string;
  onChange: (s: string) => void; show: boolean; setShow: (b: boolean) => void }) {
  return (
    <TextField fullWidth variant="outlined" label={label}
      type={show ? "text" : "password"} value={value}
      onChange={e => onChange(e.target.value)}
      InputProps={{
        startAdornment: <InputAdornment position="start">
          {label === "Current Password"
            ? <KeyRound size={20} style={{ color: "#94a3b8" }} />
            : <Lock size={20} style={{ color: "#94a3b8" }} />}
        </InputAdornment>,
        endAdornment: <InputAdornment position="end">
          <Button onClick={() => setShow(!show)}
            sx={{ minWidth: "auto", p: 1, color: "#94a3b8", "&:hover": { bgcolor: "transparent" } }}>
            {show ? <EyeOff size={20} /> : <Eye size={20} />}
          </Button>
        </InputAdornment>
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          bgcolor: "#0C0D1D",
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2B6EF5" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2B6EF5" }
        },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1E2D55" },
        "& .MuiInputLabel-root": { color: "#94a3b8" },
        "& .MuiInputBase-input": { color: "#ffffff" }
      }} />
  );
}
